import { createRoute, redirect } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IconCheck } from '@tabler/icons-react';
import { Route as appRoute } from '../../../app.js';
import { useAuthStore } from '../../../../lib/auth.js';
import { ApiError } from '../../../../lib/api.js';
import { useEmailSender, useUpdateEmailSender } from '../../../../hooks/useEmailSender.js';
import { AdminPageHeader } from '../../../../components/admin/AdminPageHeader.js';
import { Button } from '../../../../components/ui/Button.js';
import { Input } from '../../../../components/ui/Input.js';
import { Label } from '../../../../components/ui/Label.js';

export const Route = createRoute({
  getParentRoute: () => appRoute,
  path: 'admin/email-sender',
  beforeLoad: () => {
    const role = useAuthStore.getState().user?.role;
    if (role !== 'admin') throw redirect({ to: '/app' });
  },
  component: EmailSenderPage,
});

// Light client-side check; the API is the real validator.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function EmailSenderPage() {
  const settings = useEmailSender();
  const update = useUpdateEmailSender();

  const [senderName, setSenderName] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (settings.data) {
      setSenderName(settings.data.sender_name ?? '');
      setReplyTo(settings.data.reply_to ?? '');
    }
  }, [settings.data]);

  const replyToValid = replyTo.trim() === '' || EMAIL_RE.test(replyTo.trim());

  async function handleSave() {
    setError(null);
    setSaved(false);
    if (!replyToValid) {
      setError('Enter a valid reply-to email address.');
      return;
    }
    try {
      // Send empty strings as undefined-equivalent by omitting them.
      const payload: { sender_name?: string; reply_to?: string } = {};
      if (senderName.trim()) payload.sender_name = senderName.trim();
      if (replyTo.trim()) payload.reply_to = replyTo.trim();
      await update.mutateAsync(payload);
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Try again.');
    }
  }

  return (
    <div>
      <AdminPageHeader
        title="Email sender"
        description="Choose how your outgoing overviews and checklists appear to students."
      />

      {settings.isLoading && <p className="text-muted">Loading…</p>}
      {settings.error && (
        <p className="text-danger bg-danger-bg border border-danger/30 rounded-lg px-4 py-3">
          Couldn't load settings: {(settings.error as Error).message}
        </p>
      )}

      {settings.data && (
        <div className="max-w-2xl space-y-8">
          {saved && (
            <div
              className="rounded-lg px-4 py-3 text-sm flex items-center gap-2"
              style={{ backgroundColor: '#E8F3EC', color: '#1E7A47', border: '1px solid #C9E5D2' }}
            >
              <IconCheck size={16} /> <strong>Saved.</strong> New sends will use these details.
            </div>
          )}
          {error && (
            <div
              className="rounded-md px-3 py-2 text-[13px]"
              style={{ backgroundColor: '#FBEAEA', color: '#A83232', border: '1px solid #F0D0D0' }}
            >
              {error}
            </div>
          )}

          {/* Pilot note */}
          <div
            className="rounded-lg px-4 py-3 text-[13px]"
            style={{ backgroundColor: '#FCEFDC', color: '#B85C00', border: '1px solid #F2DCB8' }}
          >
            During the pilot, emails are sent from the shared Admitly address{' '}
            <span style={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace' }}>
              {settings.data.shared_from_address}
            </span>{' '}
            for reliable delivery. Students see your display name on the From line, and replies go
            to your reply-to address.
          </div>

          {/* From display name */}
          <section>
            <Label htmlFor="sender-name">From display name</Label>
            <p className="text-xs text-muted mb-2">
              The name students see in their inbox. Defaults to the Admitly default when blank.
            </p>
            <Input
              id="sender-name"
              value={senderName}
              onChange={(e) => setSenderName(e.target.value)}
              maxLength={120}
              placeholder="Aussizz Group Wollongong"
              className="max-w-md"
            />
          </section>

          {/* Reply-to */}
          <section>
            <Label htmlFor="reply-to">Reply-to address</Label>
            <p className="text-xs text-muted mb-2">
              Where student replies are delivered. Leave blank to route replies to the sending
              consultant.
            </p>
            <Input
              id="reply-to"
              type="email"
              value={replyTo}
              onChange={(e) => setReplyTo(e.target.value)}
              invalid={!replyToValid}
              placeholder="study@youragency.com.au"
              className="max-w-md"
            />
          </section>

          <div>
            <Button onClick={handleSave} loading={update.isPending} disabled={!replyToValid}>
              Save settings
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
